import { useCallback, useEffect, useState } from 'react';
import type { CameraTagCatalog, CameraTagsApi } from './cameraTagsApi';

/**
 * The tag catalogue, shared by every component on screen that needs it (filter column, camera
 * form, bulk dialogs, Settings) — one copy per scope, so a tag renamed in Settings shows up in
 * the filter without each component fetching on its own. Keyed by `api.scopeKey()`: in the
 * cloud that is the selected site, so switching site switches catalogue.
 */

const EMPTY: CameraTagCatalog = { categories: [], ptz: { yes: 0, no: 0, unknown: 0 }, colors: [] };

interface Entry { data: CameraTagCatalog | null; error: string; inflight: Promise<void> | null }
const store = new Map<string, Entry>();
const listeners = new Set<() => void>();

function entry(key: string): Entry {
  let e = store.get(key);
  if (!e) { e = { data: null, error: '', inflight: null }; store.set(key, e); }
  return e;
}

/** Re-fetch this scope's catalogue and tell every mounted user. Call after any tag change. */
export function refreshCameraTagCatalog(api: CameraTagsApi): Promise<void> {
  const key = api.scopeKey();
  const e = entry(key);
  if (!e.inflight) {
    e.inflight = api.catalog()
      .then((data) => { e.data = data || EMPTY; e.error = ''; })
      .catch((err) => { e.error = err?.response?.data?.message || err?.message || 'Could not load camera tags'; })
      .finally(() => { e.inflight = null; listeners.forEach((fn) => fn()); });
  }
  return e.inflight;
}

export function useCameraTagCatalog(api: CameraTagsApi) {
  const [, setTick] = useState(0);
  const key = api.scopeKey();
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  // First use of a scope (or a site switch) fetches it.
  useEffect(() => {
    const e = entry(key);
    if (!e.data && !e.inflight) refreshCameraTagCatalog(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // Stable per scope: callers put it in effect dependencies, and a new function every render
  // would refetch → notify → re-render → refetch forever.
  const refresh = useCallback(() => refreshCameraTagCatalog(api), [api, key]);
  const e = entry(key);
  return {
    catalog: e.data || EMPTY,
    loaded: !!e.data,
    error: e.error,
    refresh,
  };
}
